// src/data/immigrationGuides/globalTalentILR.js

const globalTalentILR = {
    slug: "global-talent-ilr",

    title: "Global Talent Visa ILR Guide UK: Settlement Requirements",

    seo: {
        metaTitle:
            "Global Talent Visa ILR UK Guide: Settlement Requirements",
        metaDescription:
            "How Global Talent visa holders qualify for ILR: the 3-year and 5-year periods, endorsing bodies, absences, English rules and the Life in the UK Test.",
        keywords: [
            "Global Talent ILR",
            "Global Talent visa settlement",
            "UK talent visa ILR"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "Global Talent ILR",
            url: "/global-talent-ilr"
        }
    ],

    introduction: `
The [Global Talent visa](https://www.gov.uk/global-talent) is for people who are leaders or potential leaders in
academia or research, arts and culture, or digital technology. Unlike the
Skilled Worker route, there is no sponsoring employer, no job offer and no
salary threshold - eligibility is based on endorsement from a designated body,
or on winning an eligible prestigious prize.

Global Talent is one of the fastest routes to [settlement](https://www.gov.uk/settle-in-the-uk/y/global-talent-visa) in the UK immigration
system. Depending on how you were endorsed, you may qualify for Indefinite
Leave to Remain (ILR) after just 3 years, rather than the 5 years required on
most other work routes.
`,

    sections: [
        {
            heading: "What is the Global Talent visa?",
            content: `
The Global Talent visa lets people aged 18 and over work in the UK for up to 5
years at a time in one of three eligible fields:

- academia or research
- arts and culture
- digital technology

There are two ways to qualify:

- **Endorsement route** - you apply to a Home Office-approved endorsing body,
  which assesses your track record and future potential in your field
- **Prize route** - if you have won an eligible prestigious prize (for
  example a Nobel Prize, or another prize on the Home Office's approved list),
  you can apply directly without going through endorsement

Endorsement is normally a two-stage process: you apply to the endorsing body
first, then apply for the visa itself once endorsed. This means separate
Home Office and endorsing-body fees.
`
        },

        {
            heading: "Endorsing bodies and fields",
            content: `
There are currently six main endorsing bodies, each responsible for assessing
applicants in specific fields:

- **The Royal Society** - natural and medical sciences
- **The British Academy** - humanities and social sciences
- **The Royal Academy of Engineering** - engineering
- **UK Research and Innovation (UKRI)** - science and innovation
- **Arts Council England** - arts and culture (including combined arts,
  dance, literature, music, theatre and visual arts)
- **Tech Nation** - digital technology

Endorsing bodies assess applicants against criteria set out in the
Immigration Rules for their field, generally looking at evidence such as
recognition as a leading figure or emerging talent, significant contributions
to the field, innovation, and endorsement or recommendation letters from
established figures in the sector. The exact evidence requirements differ by
endorsing body and by field - check the specific endorsing body's published
guidance before applying, as criteria are reviewed periodically.
`
        },

        {
            heading: "Exceptional talent vs exceptional promise",
            content: `
Within the endorsement route, applicants are assessed under one of two
categories:

- **Exceptional talent** ("recognised leader") - for applicants who are
  already established leaders in their field
- **Exceptional promise** ("emerging leader") - for applicants who show
  significant potential to become a leader in their field, but are earlier in
  their career

Which category applies affects the ILR qualifying period (see below). For
the science, engineering, humanities and research-focused endorsing bodies
(the Royal Society, British Academy, Royal Academy of Engineering and UKRI),
both exceptional talent and exceptional promise endorsements can lead to
settlement after 3 years. For Arts Council England and Tech Nation, the
distinction matters more directly: recognised leaders qualify after 3 years,
while emerging leaders qualify after 5 years.
`
        },

        {
            heading: "Global Talent ILR qualifying period",
            content: `
The Global Talent settlement qualifying period depends on which body endorsed
you and, for some fields, which category you were endorsed under:

- **3 years** - if you were endorsed by the Royal Society, the Royal Academy
  of Engineering, the British Academy or UKRI, or if you were endorsed by
  Arts Council England or Tech Nation as a recognised leader ("exceptional
  talent"), or if you qualified via the prestigious prize route
- **5 years** - if you were endorsed by Arts Council England or Tech Nation
  as an emerging leader ("exceptional promise")

Time spent on certain earlier visa categories can also count towards the
qualifying period in some cases (for example, prior time on the Tier 1
Exceptional Talent visa, the predecessor to Global Talent). Check your
specific visa history against current Immigration Rules, since eligibility
depends on exact dates and categories held.
`
        },

        {
            heading: "Residence and absences from the UK",
            content: `
As with other work-route settlement applications, you must not normally have
been outside the UK for more than 180 days in any rolling 12-month period
during your qualifying residence.

Global Talent has a notable exception: if you were endorsed by the Royal
Society, the Royal Academy of Engineering, the British Academy or UKRI (or
qualified through an eligible prize in science, engineering, humanities or
medicine), time spent outside the UK doing work related to your job may not
count towards the 180-day limit. This reflects the fact that international
travel and collaboration are often core to research careers.

Where the 180-day limit is exceeded outside this exception, the Home Office
may still exercise discretion in specific circumstances - for example
research activity, natural disasters, humanitarian crises, or other
compelling personal circumstances - but this is not guaranteed and should not
be relied on when planning your application.
`
        },

        {
            heading: "Working in your endorsed field",
            content: `
A key requirement specific to Global Talent settlement is that you must show
you have been earning money in the field in which you were endorsed during
your time on the visa. This is different from routes like Skilled Worker,
where eligibility is tied to a specific sponsoring employer and role.

If you change occupation or move into a different field after being
endorsed, this can affect your ability to meet this requirement, since the
Home Office is checking that your economic activity in the UK matches the
field your endorsement was granted for. If your career has moved into a
different field since endorsement, get advice from your endorsing body or an
immigration adviser before applying for ILR, as this is assessed on
individual circumstances.
`
        },

        {
            heading: "Life in the UK Test",
            content: `
Global Talent applicants aged 18 to 64 must normally book and pass the [Life
in the UK Test](https://www.gov.uk/life-in-the-uk-test) before applying for settlement, in the same way as applicants
on other work routes. There is no Global Talent-specific exemption from this
requirement. The test result does not expire once passed.
`
        },

        {
            heading: "English language requirement",
            content: `
Global Talent ILR applicants must also meet the standard English language
requirement for settlement, unless they qualify for an exemption. This is
normally satisfied by one of:

- being a national of a majority English-speaking country
- holding a degree taught in English (confirmed via ECCTIS/UK NARIC
  equivalency where the degree was outside the UK)
- passing an approved English language test at the required level

There is no separate, lower or different English requirement for Global
Talent compared with other settlement routes.
`
        },

        {
            heading: "Application process",
            content: `
The Global Talent ILR application process normally involves:

1. Confirming which qualifying period applies to your endorsement (3 or 5
   years) and checking your continuous residence
2. Passing the Life in the UK Test
3. Meeting the English language requirement or confirming an exemption
4. Completing the online settlement application and paying the fee
5. Providing biometrics and supporting evidence, including evidence of work
   in your endorsed field
6. Attending a UKVCAS appointment if required
7. Waiting for a Home Office decision
`
        }
    ],

    checklist: [
        "Confirm which endorsing body and category you were endorsed under",
        "Check whether your qualifying period is 3 or 5 years",
        "Review absences against the 180-day limit, including the research work exception if applicable",
        "Confirm you have been earning in your endorsed field",
        "Pass the Life in the UK Test",
        "Meet the English language requirement",
        "Prepare supporting evidence and documents"
    ],

    faqs: [
        {
            question: "Can Global Talent visa holders get ILR?",
            answer:
                "Yes. Global Talent visa holders can apply for Indefinite Leave to Remain after either 3 or 5 years, depending on which body endorsed them and, for Arts Council England and Tech Nation endorsements, whether they were endorsed as a recognised leader or an emerging leader."
        },
        {
            question: "How long does Global Talent ILR take - is it 3 or 5 years?",
            answer:
                "It depends on your endorsement. Endorsement by the Royal Society, Royal Academy of Engineering, British Academy or UKRI leads to eligibility after 3 years. Endorsement by Arts Council England or Tech Nation as a recognised leader ('exceptional talent') also qualifies after 3 years, while endorsement as an emerging leader ('exceptional promise') requires 5 years. Prestigious prize winners also qualify after 3 years."
        },
        {
            question: "Is the Life in the UK Test required for Global Talent ILR?",
            answer:
                "Yes. Applicants aged 18 to 64 must normally pass the Life in the UK Test before applying for settlement, unless they qualify for an exemption. There is no Global Talent-specific exemption from this requirement."
        },
        {
            question: "Do absences abroad affect Global Talent ILR eligibility?",
            answer:
                "You must generally not be outside the UK for more than 180 days in any rolling 12-month period. However, if you were endorsed by the Royal Society, Royal Academy of Engineering, British Academy or UKRI, time spent abroad doing work related to your job may not count towards this limit - check your circumstances carefully before applying."
        },
        {
            question: "What happens if I change field after being endorsed for Global Talent?",
            answer:
                "Global Talent settlement requires evidence that you have been earning money in the field you were endorsed for. If you have moved into a different field since endorsement, this can affect your settlement application, and you should seek advice from your endorsing body or an immigration adviser before applying."
        },
        {
            question: "Can time on other visas count towards Global Talent ILR?",
            answer:
                "In some cases, time spent on related earlier categories - such as the Tier 1 (Exceptional Talent) visa that Global Talent replaced - may count towards your qualifying period. This depends on your exact visa history and dates, so check against the current Immigration Rules or get advice on your specific case."
        }
    ],

    relatedPages: [
        {
            title: "ILR Guide",
            url: "/ilr-guide"
        },
        {
            title: "English Requirement",
            url: "/english-requirement"
        },
        {
            title: "Life in the UK Test",
            url: "/mock-exams"
        },
        {
            title: "UKVCAS Appointment Guide",
            url: "/ukvcas-appointment"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Global Talent Visa",
            url: "https://www.gov.uk/global-talent"
        },
        {
            title: "GOV.UK - Global Talent Visa Eligibility",
            url: "https://www.gov.uk/global-talent/eligibility"
        },
        {
            title: "GOV.UK - Settle in the UK: Global Talent Visa",
            url: "https://www.gov.uk/settle-in-the-uk/y/global-talent-visa"
        },
        {
            title: "GOV.UK - Global Talent Endorsing Bodies",
            url: "https://www.gov.uk/government/publications/global-talent-endorsing-bodies"
        },
        {
            title: "GOV.UK - Life in the UK Test",
            url: "https://www.gov.uk/life-in-the-uk-test"
        },
        {
            title: "GOV.UK - Immigration Rules",
            url: "https://www.gov.uk/guidance/immigration-rules"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-31"
    }
};

export default globalTalentILR;
